import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { authenticationApi } from "pages/authentication/authenticationService";
import { AuthenticationResponse, AuthDeviceDetails, loginApi } from "pages/login/loginService";
import { mfaApi, MfaResponse } from "pages/MFA/mfaService";
import { RegisterResponse, registrationApi } from "pages/registration/registrationService";
import type { RootState } from "store";

/** Matches PingOne MFA_OTP_STATE_CACHE_EXP_IN_MIN (15 minutes). */
export const MFA_CHALLENGE_TTL_MS = 15 * 60 * 1000;

export type PendingMfaRoute = "/authcode" | "/mfa" | "/registrationAuth";

interface MfaState {
  flowId: string;
  mfaDevices: AuthDeviceDetails[];
  mfaResponse: MfaResponse;
  resendCodeDeviceId: string;
  authDeviceType: string;
  challengeUserId: string;
  challengeExpiresAt: number | null;
}

const initialState: MfaState = {
  flowId: "",
  mfaDevices: [],
  mfaResponse: {
    id: "",
  },
  resendCodeDeviceId: "",
  authDeviceType: "",
  challengeUserId: "",
  challengeExpiresAt: null,
};

const applyChallengeFromLoginResponse = (state: MfaState, payload: AuthenticationResponse): void => {
  if (payload.status !== "OTP_REQUIRED" && payload.status !== "DEVICE_SELECTION_REQUIRED") {
    return;
  }
  state.challengeUserId = payload.id ?? "";
  state.challengeExpiresAt = Date.now() + MFA_CHALLENGE_TTL_MS;

  if (payload.status === "OTP_REQUIRED" && payload.flowId) {
    state.mfaResponse = { id: payload.flowId };
    state.resendCodeDeviceId = payload._embedded?.devices?.[0]?.id ?? "";
    state.flowId = payload.flowId;
    state.mfaDevices = payload._embedded?.devices ?? [];
  } else if (payload.status === "OTP_REQUIRED" && !payload.flowId) {
    state.resendCodeDeviceId = payload.deviceAuthId ?? "";
    state.authDeviceType = payload.deviceType ?? "";
  } else if (payload.status === "DEVICE_SELECTION_REQUIRED") {
    state.flowId = payload.flowId;
    state.mfaDevices = payload._embedded?.devices ?? [];
  }
};

const applyChallengeFromRegistrationResponse = (state: MfaState, payload: RegisterResponse): void => {
  if (payload.status === "OTP_REQUIRED") {
    state.challengeUserId = payload.id ?? "";
    state.challengeExpiresAt = Date.now() + MFA_CHALLENGE_TTL_MS;
    state.resendCodeDeviceId = payload.deviceAuthId ?? "";
    state.authDeviceType = payload.deviceType ?? "";
    return;
  }
  if (payload.status === "DEVICE_SELECTION_REQUIRED") {
    state.challengeUserId = payload.id ?? "";
    state.challengeExpiresAt = Date.now() + MFA_CHALLENGE_TTL_MS;
    if ("_embedded" in payload && payload._embedded) {
      state.flowId = payload.flowId;
      state.mfaDevices = payload._embedded?.devices ?? [];
    }
  }
};

const mfaSlice = createSlice({
  name: "mfaState",
  initialState,
  reducers: {
    setResendCodeDeviceId: (state, action: PayloadAction<string>) => {
      state.resendCodeDeviceId = action.payload;
    },
    setAuthDeviceType: (state, action: PayloadAction<string>) => {
      state.authDeviceType = action.payload;
    },
    setMfaResponse: (state, action: PayloadAction<MfaResponse>) => {
      state.mfaResponse = action.payload;
      if (action.payload.id) {
        state.challengeExpiresAt = Date.now() + MFA_CHALLENGE_TTL_MS;
      }
    },
    recordMfaChallenge: (state, action: PayloadAction<{ userId?: string } | undefined>) => {
      state.challengeExpiresAt = Date.now() + MFA_CHALLENGE_TTL_MS;
      if (action.payload?.userId) {
        state.challengeUserId = action.payload.userId;
      }
    },
    clearMfaChallenge: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addMatcher(loginApi.endpoints.postLogin.matchFulfilled, (state, action: PayloadAction<AuthenticationResponse>) => {
      applyChallengeFromLoginResponse(state, action.payload);
    });

    builder.addMatcher(registrationApi.endpoints.postRegistration.matchFulfilled, (state, action: PayloadAction<RegisterResponse>) => {
      applyChallengeFromRegistrationResponse(state, action.payload);
      if ("_embedded" in action.payload && action.payload._embedded && action.payload.status !== "OTP_REQUIRED") {
        state.flowId = action.payload.flowId;
        state.mfaDevices = action.payload._embedded?.devices ?? [];
      }
    });

    builder.addMatcher(mfaApi.endpoints.postMfa.matchFulfilled, (state, action: PayloadAction<MfaResponse>) => {
      state.mfaResponse = action.payload;
      state.challengeExpiresAt = Date.now() + MFA_CHALLENGE_TTL_MS;
    });

    builder.addMatcher(authenticationApi.endpoints.postOtpValidation.matchFulfilled, () => initialState);
  },
});

export const isMfaChallengeValid = (state: RootState): boolean => {
  const expiresAt = state.mfaState.challengeExpiresAt;
  return expiresAt != null && expiresAt > Date.now();
};

export const getPendingMfaRoute = (state: RootState): PendingMfaRoute | null => {
  if (!isMfaChallengeValid(state)) {
    return null;
  }
  const { mfaResponse, flowId, mfaDevices, authDeviceType, resendCodeDeviceId } = state.mfaState;
  if (mfaResponse.id) {
    return "/authcode";
  }
  if (authDeviceType || (resendCodeDeviceId && !flowId)) {
    return "/registrationAuth";
  }
  if (flowId && mfaDevices.length > 0) {
    return "/mfa";
  }
  return null;
};

export const getMfaFlowId = (state: RootState): string => state.mfaState.flowId;
export const getMfaDevices = (state: RootState): AuthDeviceDetails[] => state.mfaState.mfaDevices;
export const getAuthOtpDetails = (state: RootState): MfaResponse => state.mfaState.mfaResponse;
export const getResendCodeDeviceId = (state: RootState): string => state.mfaState.resendCodeDeviceId;
export const getAuthDeviceType = (state: RootState): string => state.mfaState.authDeviceType;
export const getMfaChallengeUserId = (state: RootState): string => state.mfaState.challengeUserId;

export const { setResendCodeDeviceId, setAuthDeviceType, setMfaResponse, recordMfaChallenge, clearMfaChallenge } = mfaSlice.actions;

export default mfaSlice.reducer;
